<template>
  <component
    :is="tag || 'button'"
    class="balance-status-button"
    :class="{
      desktop: $isDesktop,
      'urgent-action-required': urgentActionRequired,
    }"
  >
    <img
      loading="lazy"
      src="@/assets/images/warning-red.svg"
      width="14"
      height="13"
    />
    <div v-if="paymentInfo.remainder.amountMinor > 0">
      <i18n-t keypath="Balance {price}" class="balance-amount">
        <template #price>
          <pu-price
            :price="getMoney(paymentInfo.remainder)"
            :display-original-price="false"
          />
        </template>
      </i18n-t>

      <div
        v-if="paymentInfo.payment && paymentInfo.payment.dueDate"
        class="deposit-terms"
      >
        <translate>Due:</translate>
        <pu-date
          :date="paymentInfo.payment.dueDate"
          :options="{
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          }"
        />
      </div>

      <div
        v-else-if="
          paymentInfo.payment &&
          (paymentInfo.payment.timing?.toLowerCase() === 'advance' ||
            paymentInfo.payment.timing?.toLowerCase() === 'arrive')
        "
        class="deposit-terms"
      >
        <translate>Due:</translate>
        <template
          v-if="paymentInfo.payment.timing?.toLowerCase() === 'advance'"
        >
          <template v-if="paymentInfo.payment.dueDate">
            <pu-date
              :date="paymentInfo.payment.dueDate"
              :options="{
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              }"
            />
          </template>
          <translate v-else>ASAP</translate>
        </template>
        <template
          v-else-if="paymentInfo.payment.timing?.toLowerCase() === 'arrive'"
        >
          <translate>On arrival</translate>
        </template>
      </div>

      <div
        v-else-if="
          !paymentInfo.stripecampsitepayment &&
          paymentInfo.payment.timing === undefined &&
          paymentInfo.payment.dueDate === null
        "
        class="deposit-terms"
      >
        <translate>Due:</translate>
        <translate>On arrival</translate>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import {
  MoneyType,
  SimpleMoney,
  simpleMoneyToMoney,
} from '../../../utility/money'

const props = defineProps<{
  tag?: string
  paymentInfo: {
    remainder: SimpleMoney
    payment: {
      dueDate: string | null
      timing: string | null
      status: string | null
      statusDisplay: string | null
    }
  }
}>()

const urgentActionRequired = computed(() => {
  const payment = props.paymentInfo.payment
  const paymentStatus = props.paymentInfo.payment?.status?.toLowerCase()
  const validStatuses = ['reminder_sent', 'expired_sent', 'error', 'error_sent']
  return payment && paymentStatus && validStatuses.includes(paymentStatus)
})

function getMoney(price: SimpleMoney): MoneyType {
  return simpleMoneyToMoney(price)
}
</script>

<style scoped>
.balance-status-button {
  align-items: flex-start;
  background-color: var(--colour-dark-blue);
  border-radius: 0.3em;
  border: none;
  box-shadow: none;
  color: white;
  display: flex;
  flex-shrink: 0;
  font-size: 0.75em;
  gap: 0.4em;
  justify-content: flex-start;
  line-height: 1;
  padding: 0.25em 0.6em 0.4em 0.4em;
  text-decoration: none;
}

.balance-amount {
  display: flex;
  align-items: center;
  gap: 0.3em;
  white-space: nowrap;
}

.balance-status-button :deep(.display-price) {
  color: var(--colour-light-blue);
}

.deposit-terms {
  font-size: var(--small);
  font-weight: 400;
  margin-top: 0.3em;
  text-align: left;
  text-transform: uppercase;
}

.urgent-action-required .deposit-terms {
  margin-top: 0.4em;
}

.urgent-action-required .deposit-terms time {
  background-color: var(--colour-red);
  border-radius: 0.6em;
  font-weight: bold;
  padding: 1px 4px;
}

.balance-status-button img {
  filter: hue-rotate(44deg) brightness(2);
  padding-top: 0.2em;
}

.balance-status-button.urgent-action-required img {
  filter: hue-rotate(0deg) brightness(1.25);
}

.balance-status-button.desktop {
  font-size: var(--title);
}
</style>
